import clsx from 'clsx';
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useFocusTrap } from '../../hooks/useFocusTrap';

import styles from './Modal.module.css';

export interface ModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly icon?: ReactNode;
  readonly title: string;
  readonly subtitle?: string;
  readonly size?: 'sm' | 'md' | 'lg';
  readonly children: ReactNode;
  readonly footer?: ReactNode;
}

export function Modal({
  open,
  onClose,
  icon = '✦',
  title,
  subtitle,
  size = 'md',
  children,
  footer,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, open);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className={styles.overlay}>
      {/* A real button, so dismissing by backdrop is keyboard- and AT-reachable. */}
      <button
        type="button"
        className={styles.backdrop}
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        // Focusable only programmatically, so the trap can seat focus here.
        tabIndex={-1}
        aria-label={title}
        className={clsx(styles.modal, size !== 'md' && styles[size])}
      >
        <header className={styles.header}>
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
          <div>
            <h2 className={styles.title}>{title}</h2>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close dialog"
          >
            ✕
          </button>
        </header>
        <div className={styles.body}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
